/**
 * Tracked sharing — the portable half.
 *
 * Adopts the portfolio play "Sweep the tracked-sharing (share.ts) module to
 * every product with public shareable pages". Deliberately free of React and
 * anything Devotion Fund specific: the same two problems show up on every
 * product and only the surface names differ.
 *
 *   1. A shared link has to be attributable, or "does sharing work?" stays an
 *      opinion. DF-123 tagged links with `?ref=share`, which only this repo
 *      understands — PostHog's channel report, Google Analytics and Search
 *      Console all read UTM parameters and nothing else, so every arrival
 *      from a shared link was filed as direct traffic everywhere it mattered.
 *   2. The share ACT and the share ARRIVAL are different events with
 *      different denominators. Clicks measure enthusiasm; arrivals measure
 *      reach, and only the pair says whether the people being sent are worth
 *      anything.
 */

/** utm_source value that marks a visit as arriving from one of our links. */
export const SHARE_SOURCE = 'share';

/**
 * Tag an outbound share link so the arrival can be read back.
 *
 * campaign is the creator: per-creator attribution is the whole point of
 * sharing on this product, and a generic campaign would throw it away.
 */
export function buildShareUrl(absoluteUrl: string, medium: string, campaign: string): string {
  try {
    const u = new URL(absoluteUrl);
    u.searchParams.set('utm_source', SHARE_SOURCE);
    u.searchParams.set('utm_medium', medium);
    u.searchParams.set('utm_campaign', campaign);
    return u.toString();
  } catch {
    // Never break a share button over a malformed URL — an untagged link is
    // worse than a tagged one, and no link at all is worse than both.
    return absoluteUrl;
  }
}

/**
 * Did this visit arrive from a shared link, and from where?
 *
 * Also accepts DF-123's `?ref=share`, because those links are already out
 * there in people's messages and inboxes and will keep arriving for months.
 * Dropping them on the day the scheme changed would have looked exactly like
 * sharing suddenly stopping.
 *
 * Returns null for everything else, so the caller fires nothing rather than
 * an event with empty properties — an "unknown" bucket that swallows organic
 * traffic is worse than no row at all.
 */
export function readShareArrival(
  search: string
): { medium: string; campaign: string } | null {
  const p = new URLSearchParams(search);
  if (p.get('utm_source') === SHARE_SOURCE) {
    return {
      medium: p.get('utm_medium') || 'unknown',
      campaign: p.get('utm_campaign') || 'unknown',
    };
  }
  if (p.get('ref') === SHARE_SOURCE) {
    return { medium: 'legacy_ref', campaign: 'unknown' };
  }
  return null;
}
